import { createContext, Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { Steps } from 'antd'
import { isArray, isFunction, isObject, values } from 'lodash'
import { faFileEdit, faGraduationCap, faSchool, faUserGraduate } from '@fortawesome/pro-duotone-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-solid-svg-icons'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import Cookie from 'js-cookie'

import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { getIdOrNull, isNonEmptyArray, isOptimistic, isUUID, safeSpread } from '../../../utils'
import { Properties } from '../../../utils/entities'
import useRequest from '../../../hooks/useRequest'
import { AS_OBJECT, getAllSections } from '../../../utils/api/section'
import { getAllCountries } from '../../../utils/api/countries'
import { getAllSectionToYears } from '../../../utils/api/sectionToYear'
import WithFormFooter from '../../../HOC/Forms/WithFormFooter'
import StudentForm from '../StudentForm/StudentForm'
import { Permissions } from '../StudentForm/SearchStudentModalProvider'
import SchoolForm from './School/SchoolForm'
import { School, SchoolValidators } from '../../../utils/entities/school'
import { Student, StudentProperties, StudentValidators } from '../../../utils/entities/student'
import { isValid } from '../../../utils/validators'
import { reduceBySection } from '../../../utils/entities/sectionToYear'
import InternshipRequestForm from './InternshipRequestForm'
import { InternshipRequest, mergeWithStudent, Origins, PortalInternshipStepValidators } from '../../../utils/entities/internshipRequest'
import { selectActiveLanguage } from '../../../reducers/LanguageReducer'
import { getPortalHospitalDepartments } from '../../../utils/api/hospitalDepartment'
import { ACCESS_KEY } from '../../../utils/api/internshipRequestPortal'
import { Institution } from '../../../utils/entities/institution'
import { DEFAULT_DATA as DEFAULT_FILES } from './InternshipRequestFilesForm'
import { getAllLanguages } from '../../../utils/api/language'
import { getAllSchoolsPublic, publicCreateSchoolFromPortal } from '../../../utils/api/school'
import SchoolSelector from './School/SchoolSelector'
import { Formats, SchoolSection } from '../../../utils/entities/schoolSection'
import { getInstitutionOptionsEnabledByInstitution } from '../../../utils/api/institution'
import { InstitutionOptions } from '../../../utils/constants'
import { InternshipFileTypes } from '../../../utils/entities/internshipRequestFile'
import { publicAddInternshipRequestFile, publicCreateInternshipRequest } from '../../../utils/api/internshipRequest'
import { ARE_PARENT_EXCLUDED, getAllSchoolSectionsPublic } from '../../../utils/api/schoolSection'
import { usePublicRequest } from '../../../hooks/usePublicRequest'
import { getMetadataByInstitution } from '../../../utils/api/internshipRequestMetadata'
import { getRedirectAction } from '../../../utils/api/student'
import { Routes } from '../../../routes'
import { RequestPortalContext } from '../../RequestPortalProvider'
import { StorageKeys } from '../../AuthProvider'
import RedirectStudentModal from './RedirectStudentModal'
import { Cookies } from '../../../utils/Cookie'
import { displayErrorMessage, onSuccess } from '../../../utils/apiHelper'
import UnderConstruction from '../../../Components/shared/UnderConstruction'

export const InternshipRequestPortalFormContext = createContext()

export const Modes = Object.freeze({
  STUDENT: 'student',
  SCHOOL: 'school'
})

export const Forms = Object.freeze({
  INTERNSHIP: Properties.INTERNSHIP,
  SCHOOL: Properties.SCHOOL,
  SECTIONS: Properties.SECTIONS,
  STUDENT: Properties.STUDENT
})

const STYLES = {
  next: {
    icon: faAngleRight,
    className: 'logo-colored-button blue',
    title: 'common.step.next'
  },
  previous: {
    icon: faAngleLeft,
    className: 'logo-colored-button green',
    title: 'common.step.previous'
  }
}

export const DEFAULT_DATA = {
  [Modes.SCHOOL]: {
    [Forms.SCHOOL]: new School(),
    [Forms.SECTIONS]: []
  },
  [Modes.STUDENT]: {
    [Forms.STUDENT]: new Student({ language: null }),
    [Forms.SCHOOL]: new School(),
    [Forms.INTERNSHIP]: new InternshipRequest({ requestOrigin: Origins.WEB_PORTAL, metadata: {} })
  }
}

export const SUBMIT_REQUESTS = {
  [Modes.SCHOOL]: publicCreateSchoolFromPortal,
  [Modes.STUDENT]: publicCreateInternshipRequest
}

const DEFAULT_FORM_HANDLER = { data: null, callback: null, mode: null }
const REQUESTED_INSTITUTION_OPTIONS = [InstitutionOptions.RESUME, InstitutionOptions.COVER_LETTER]

const InternshipRequestPortalFormProvider = ({
  children, allowSchoolCreation = false, institution, institutionGroup, portalAccessKey, visible = false, Wrapper = Fragment, onReset, initialMode = null
}) => {
  const { setStudentEmail } = useContext(RequestPortalContext)

  const t = useSelector(selectTranslate)
  const language = useSelector(selectActiveLanguage)
  const history = useHistory()

  const buildDefaultFormHandler = useCallback(mode => (mode && DEFAULT_DATA[mode])
    ? { data: DEFAULT_DATA[mode], callback: null, mode }
    : { ...DEFAULT_FORM_HANDLER }
  , [])

  const [formHandler, setFormHandler] = useState(() => buildDefaultFormHandler(initialMode))
  const [internshipFiles, setInternshipFiles] = useState(DEFAULT_FILES)
  const [institutionOptions, setInstitutionOptions] = useState([])
  const [redirectAction, setRedirectAction] = useState('')

  const mandatoryFields = useMemo(() => institutionOptions.reduce((acc, option) => {
    if (option.optionType.type === InstitutionOptions.RESUME && option.optionEnabled) {
      acc[InternshipFileTypes.RESUME] = true
    } else if (option.optionType.type === InstitutionOptions.COVER_LETTER && option.optionEnabled) {
      acc[InternshipFileTypes.COVER_LETTER] = true
    }
    return acc
  }, {}), [institutionOptions])

  const externalHandlerSetter = formHandler => {
    if (isObject(formHandler.data) && formHandler.mode === Modes.STUDENT && getIdOrNull(institution)) {
      formHandler.data[Forms.INTERNSHIP] = new InternshipRequest({ ...formHandler.data[Forms.INTERNSHIP], institution: new Institution(institution) })
    }

    setFormHandler(formHandler)
  }

  const [currentStep, setCurrentStep] = useState(0)
  const [errorFields, setErrorFields] = useState([])

  useEffect(() => {
    setFormHandler(buildDefaultFormHandler(initialMode))
    setErrorFields([])
    setInternshipFiles(DEFAULT_FILES)
    setCurrentStep(0)
  }, [buildDefaultFormHandler, initialMode])

  const countries = useRequest({
    request: getAllCountries,
    parameters: useMemo(() => ({}), [])
  })

  const languages = useRequest({
    request: getAllLanguages,
    parameters: useMemo(() => ({}), [])
  })

  const schools = useRequest({
    request: getAllSchoolsPublic,
    parameters: useMemo(() => ({}), [])
  })

  const schoolSections = useRequest({
    request: getAllSchoolSectionsPublic,
    isPublicCall: true,
    parameters: useMemo(() => ({
      school: getIdOrNull(formHandler.data?.school),
      [ARE_PARENT_EXCLUDED]: true
    }), [formHandler.data?.school]),
    enabled: isObject(formHandler.data?.school) && !isOptimistic(getIdOrNull(formHandler.data?.school))
  })

  const sections = useRequest({
    request: getAllSections,
    isPublicCall: true,
    parameters: useMemo(() => ({ [AS_OBJECT]: false }), [])
  })

  const sectionToYears = useRequest({
    request: getAllSectionToYears,
    isPublicCall: true,
    parameters: useMemo(() => ({ paginate: false }), [])
  })

  const metadataFields = useRequest({
    request: usePublicRequest(getMetadataByInstitution),
    parameters: useMemo(() => institution, [institution]),
    enabled: getIdOrNull(institution) !== null
  })

  const availableSectionsAndYears = useMemo(() => {
    const available = {
      sections: sections.data,
      sectionToYears: sectionToYears.data
    }

    if (isNonEmptyArray(schoolSections.data)) {
      const filteredKeys = schoolSections.data.reduce((acc, ssec) => {
        if (ssec.sectionToYear !== null) {
          const sectionToYear = available.sectionToYears.find(sty => getIdOrNull(sty) === getIdOrNull(ssec.sectionToYear))

          acc.sections[getIdOrNull(sectionToYear.section)] = true
          acc.sectionToYears[getIdOrNull(sectionToYear)] = true
        }

        return acc
      }, { sections: {}, sectionToYears: {} })

      available.sections = available.sections.filter(s => filteredKeys.sections[getIdOrNull(s)])
      available.sectionToYears = available.sectionToYears.filter(sty => filteredKeys.sectionToYears[getIdOrNull(sty)])
    }

    return {
      sections: available.sections,
      sectionToYears: reduceBySection(available.sectionToYears)
    }
  }, [schoolSections.data, sections.data, sectionToYears.data])

  useEffect(() => {
    let active = true

    if (isObject(institution) && !isOptimistic(institution)) {
      getInstitutionOptionsEnabledByInstitution(institution, REQUESTED_INSTITUTION_OPTIONS).then(({ data }) => {
        if (active) {
          setInstitutionOptions(data)
        }
      }).catch(() => {
        if (active) {
          setInstitutionOptions([])
        }
      })
    }

    return () => {
      active = false
    }
  }, [institution])

  const requestInstitution = useMemo(() => getIdOrNull(formHandler.data?.[Forms.INTERNSHIP]?.institution)
    ? formHandler.data[Forms.INTERNSHIP].institution
    : institution
  , [formHandler.data?.[Forms.INTERNSHIP]?.institution, institution])

  const hospitalDepartments = useRequest({
    request: getPortalHospitalDepartments,
    parameters: useMemo(() => ({
      institution: getIdOrNull(requestInstitution),
      [ACCESS_KEY]: portalAccessKey,
      paginate: false
    }), [requestInstitution, portalAccessKey]),
    enabled: formHandler.mode === Modes.STUDENT && (getIdOrNull(requestInstitution) !== null) && portalAccessKey !== null
  })

  const updateKey = key => value => setFormHandler(formHandler => ({
    ...formHandler,
    data: {
      ...formHandler.data,
      [key]: { ...safeSpread(formHandler.data[key]), ...value }
    }
  }))

  const searchStudent = email => getRedirectAction(email).then(({ data }) => {
    setRedirectAction(data)
    setStudentEmail(email)
  }).catch(_ => undefined)

  const handleSelectSchool = value => {
    setFormHandler(formHandler => ({
      ...formHandler,
      data: {
        ...formHandler.data,
        [Properties.SCHOOL]: value
      }
    }))

    if (!isOptimistic(getIdOrNull(value))) {
      setErrorFields([])
    }
  }

  const handleMetadataChange = (fieldName, value) => {
    setFormHandler(formHandler => ({
      ...formHandler,
      data: {
        ...formHandler.data,
        [Forms.INTERNSHIP]: {
          ...formHandler.data[Forms.INTERNSHIP],
          [Properties.METADATA]: {
            ...formHandler.data[Forms.INTERNSHIP][Properties.METADATA],
            [fieldName]: value
          }
        }
      }
    }))
  }

  const isValidStep = validation => isFunction(validation) ? validation().keys.length === 0 : true

  const nextStep = validation => () => {
    if (isValidStep(validation)) {
      setCurrentStep(step => step + 1)
    }
  }

  const previousStep = () => setCurrentStep(step => Math.max(step - 1, 0))
  const resetHandler = () => {
    setErrorFields([])
    setFormHandler(buildDefaultFormHandler(initialMode))
    setInternshipFiles(DEFAULT_FILES)

    if (isFunction(onReset)) {
      onReset()
    }
  }

  const validatedInternshipForm = (data, validators) => () => {
    const errors = validateForm(data, validators)()

    if (mandatoryFields[InstitutionOptions.RESUME] && internshipFiles[InternshipFileTypes.RESUME].length === 0) {
      errors.keys.push(InternshipFileTypes.RESUME)
    }

    if (mandatoryFields[InstitutionOptions.COVER_LETTER] && internshipFiles[InternshipFileTypes.COVER_LETTER].length === 0) {
      errors.keys.push(InternshipFileTypes.COVER_LETTER)
    }

    return errors
  }

  const validateForm = (data, validators) => () => {
    const errors = isValid(data, values(validators))

    setErrorFields(errors.keys)

    return errors
  }

  const validateSchool = useCallback(() => {
    let errors = { keys: [] }

    if (allowSchoolCreation) {
      errors = validateForm(formHandler.data[Forms.SCHOOL], values(SchoolValidators))()
    } else {
      errors = validateForm(formHandler.data[Forms.SCHOOL], values({ id: s => isUUID(getIdOrNull(s[Properties.ID])) }))()
    }

    return errors
  }, [formHandler.data?.[Forms.SCHOOL]])

  const handleFormSubmit = validation => e => {
    e.preventDefault()

    if (!isValidStep(validation)) {
      return
    }

    const entity = { name: '', id: '' }

    if (institutionGroup) {
      entity.name = Properties.INSTITUTION_GROUP
      entity.id = getIdOrNull(institutionGroup)
    } else if (institution) {
      entity.name = Properties.INSTITUTION
      entity.id = getIdOrNull(institution)
    }

    const portalData = {
      [entity.name]: entity.id,
      [ACCESS_KEY]: portalAccessKey
    }

    const toSubmit = { data: null }

    if (formHandler.mode === Modes.STUDENT) {
      const requestFiles = Object.keys(internshipFiles).reduce(
        (acc, type) => acc.concat(internshipFiles[type].flatMap(file => ({ file, type }))), [])

      const { school, student, internship } = formHandler.data

      const request = new InternshipRequest({ ...internship, institution: requestInstitution, school: school, files: requestFiles })
      const internshipRequest = mergeWithStudent(request, new Student({ ...student, language: language.code }))
      /** @todo remove "student" key in request body if student is optimistic */
      internshipRequest.student = null

      toSubmit.data = internshipRequest.toRequestBody()
    } else if (formHandler.mode === Modes.SCHOOL) {
      const { school, sections } = formHandler.data

      toSubmit.data = {
        ...portalData,
        ...school.toRequestBody(Formats.PUBLIC_CREATE),
        sections: sections.map(s => new SchoolSection(s).toRequestBody(Formats.PUBLIC_CREATE))
      }
    }

    SUBMIT_REQUESTS[formHandler.mode]({ ...toSubmit.data, [ACCESS_KEY]: portalAccessKey }).then(({ data }) => {
      if (formHandler.mode === Modes.STUDENT) {
        const promisesStack = []

        if (isArray(toSubmit.data.files)) {
          toSubmit.data.files.forEach(f => {
            f[ACCESS_KEY] = portalAccessKey
            promisesStack.push(publicAddInternshipRequestFile(data, f))
          })
        }
      }
    }).then(() => {
      if (isFunction(formHandler.callback)) {
        formHandler.callback(toSubmit.data)
      }

      resetHandler()
      setCurrentStep(0)

      onSuccess(t('internship_request_portal.submit.success'))
    }).catch(reason => {
      console.error(reason)

      displayErrorMessage(reason, t)
    })
  }

  const steps = useMemo(() => {
    if (!(
      isNonEmptyArray(countries.data) &&
      isNonEmptyArray(sections.data) &&
      isObject(sectionToYears.data))
    ) return []

    let steps = []

    if (formHandler.mode === Modes.SCHOOL) {
      steps = steps.concat([
        {
          form: Forms.SCHOOL,
          title: t('common.school'),
          icon: faSchool
        },
        {
          form: Forms.SECTIONS,
          title: t('common.sections'),
          icon: faGraduationCap
        }
      ])
    } else if (formHandler.mode === Modes.STUDENT) {
      steps = steps.concat([
        {
          form: Forms.STUDENT,
          title: t('common.student'),
          icon: faUserGraduate
        },
        {
          form: Forms.SCHOOL,
          title: t('common.school'),
          icon: faSchool
        },
        {
          form: Forms.INTERNSHIP,
          title: t('common.internship'),
          icon: faFileEdit
        }
      ])
    }

    return steps
  }, [formHandler.mode, formHandler.data, countries.data, sections.data, sectionToYears.data, t])

  const studentValidators = useMemo(() => {
    const validators = { ...StudentValidators }
    delete validators[StudentProperties.SECTIONS]

    return validators
  }, [])

  return (
    <>
      <RedirectStudentModal
        redirectAction={redirectAction}
        onCancel={() => {
          updateKey(Forms.STUDENT)({ [Properties.EMAIL]: '' })
          setRedirectAction('')
        }}
        onConfirm={() => {
          Cookie.remove(Cookies.TOKEN)
          window.localStorage.removeItem(StorageKeys.ACCOUNT_ID)
          history.push(Routes.LOGIN)
        }}
      />

      <InternshipRequestPortalFormContext.Provider value={{ formHandler, setFormHandler: externalHandlerSetter }}>
        {children}
      </InternshipRequestPortalFormContext.Provider>
      {visible && (
        <Wrapper>
          {formHandler.data && isNonEmptyArray(steps) && (
            <Steps current={currentStep} type='navigation'>
              {steps.map(item => (
                <Steps.Step key={item.form} title={item.title} icon={<FontAwesomeIcon icon={item.icon} />} />
              ))}
            </Steps>
          )}
          {/** @todo UNDER_CONSTRUCTION */}
          {false && formHandler.mode === Modes.SCHOOL && isNonEmptyArray(steps) && currentStep !== null && (
            <>
              <WithFormFooter
                visible={currentStep === 0}
                confirmClassName={STYLES.next.className}
                confirmTitle={STYLES.next.title}
                confirmIcon={STYLES.next.icon}
                onConfirm={nextStep(validateForm(
                  formHandler.data[Forms.SCHOOL],
                  values(SchoolValidators)
                ))}
                onCancel={resetHandler}
              >
                <SchoolForm
                  errorFields={errorFields}
                  displayTitle={false}
                  countries={countries.data}
                  data={formHandler.data[Forms.SCHOOL]}
                  onChange={updateKey(Forms.SCHOOL)}
                />
              </WithFormFooter>
            </>
          )}
          {/** @todo UNDER_CONSTRUCTION */}
          {formHandler.mode === Modes.SCHOOL && isNonEmptyArray(steps) && currentStep !== null && (
            <WithFormFooter visible onCancel={resetHandler}>
              <UnderConstruction />
            </WithFormFooter>
          )}
          {formHandler.mode === Modes.STUDENT && isNonEmptyArray(steps) && currentStep !== null && (
            <>
              <WithFormFooter
                visible={currentStep === 0}
                confirmClassName={STYLES.next.className}
                confirmTitle={STYLES.next.title}
                confirmIcon={STYLES.next.icon}
                onConfirm={nextStep(validateForm(
                  formHandler.data[Forms.STUDENT],
                  values(studentValidators)
                ))}
                onCancel={resetHandler}
              >
                <div className='pd-8'>
                  <StudentForm
                    errorFields={errorFields}
                    languages={languages.data}
                    permission={Permissions.WRITE}
                    data={formHandler.data[Forms.STUDENT]}
                    onChange={updateKey(Forms.STUDENT)}
                    onEmailChange={searchStudent}
                  />
                </div>
              </WithFormFooter>

              <WithFormFooter
                cancelTitle={STYLES.previous.title}
                cancelIcon={STYLES.previous.icon}
                visible={currentStep === 1}
                confirmClassName={STYLES.next.className}
                confirmTitle={STYLES.next.title}
                confirmIcon={STYLES.next.icon}
                onConfirm={nextStep(validateSchool)}
                onCancel={previousStep}
              >
                {allowSchoolCreation ? (
                  <SchoolForm
                    errorFields={errorFields}
                    displayTitle={false}
                    countries={countries.data}
                    data={formHandler.data[Forms.SCHOOL]}
                    onChange={updateKey(Forms.SCHOOL)}
                  />
                ) : (
                  <SchoolSelector
                    title={t('internship_request_portal.selectors.school.title')}
                    errorFields={errorFields}
                    schools={schools.data}
                    data={formHandler.data[Forms.SCHOOL]}
                    onChange={handleSelectSchool}
                  />
                )}
              </WithFormFooter>

              <WithFormFooter
                visible={currentStep === 2}
                cancelTitle={STYLES.previous.title}
                cancelIcon={STYLES.previous.icon}
                confirmClassName={STYLES.previous.className}
                onConfirm={handleFormSubmit(validatedInternshipForm(
                  formHandler.data[Forms.INTERNSHIP],
                  values(PortalInternshipStepValidators)
                ))}
                onCancel={previousStep}
              >
                <InternshipRequestForm
                  data={formHandler.data[Forms.INTERNSHIP]}
                  displayTitle={false}
                  errorFields={errorFields}
                  files={internshipFiles}
                  hospitalDepartments={hospitalDepartments.data}
                  institution={institution}
                  institutionGroup={institutionGroup}
                  requiredFields={mandatoryFields}
                  sections={availableSectionsAndYears.sections}
                  sectionToYears={availableSectionsAndYears.sectionToYears}
                  metadataFields={metadataFields.data}
                  onMetadataChange={handleMetadataChange}
                  onChange={updateKey(Forms.INTERNSHIP)}
                  setFiles={setInternshipFiles}
                />
              </WithFormFooter>
            </>
          )}
        </Wrapper>
      )}
    </>
  )
}

export default InternshipRequestPortalFormProvider
